import React, { useRef, useEffect, FunctionComponent } from 'react';
import { Button as ButtonBase, Flex, Box } from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import { ReactComponent as ExternalLink } from '../../assets/external-link.svg';

export interface ButtonProps {
  onClick: () => void;
  variant: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'inlineLink';
  children: React.ReactNode;
  disabled?: boolean;
  smallSized?: boolean;
  [key: string]: any;
  isFocused?: boolean;
}

export interface ButtonLinkProps {
  onClick?: () => void;
  link: string;
  variant: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'inlineLink';
  children: React.ReactNode;
  disabled?: boolean;
  smallSized?: boolean;
  [key: string]: any;
  isFocused?: boolean;
}

interface ButtonItemProps {
  disabled?: boolean;
  smallSized?: boolean;
  variant: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'inlineLink';
  isFocused?: boolean;
}

interface LinkProps {
  disabled?: boolean;
  smallSized?: boolean;
  variant: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'inlineLink';
  isFocused?: boolean;
}

const fontColor = theme.variants('mode', 'variant', {
  primary: {
    light: (props: any) => props.theme.colors.white,
    dark: (props: any) => props.theme.colors.white,
  },
  secondary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.white,
  },
  tertiary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.white,
  },
  ghost: {
    light: (props: any) => props.theme.colors.periwinkle,
    dark: (props: any) => props.theme.colors.brightCerulean,
  },
  inlineLink: {
    light: (props: any) => props.theme.colors.periwinkle,
    dark: (props: any) => props.theme.colors.brightCerulean,
  },
});

const hoverFontColor = theme.variants('mode', 'variant', {
  primary: {
    light: (props: any) => props.theme.colors.white,
    dark: (props: any) => props.theme.colors.white,
  },
  secondary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.white,
  },
  tertiary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.white,
  },
  ghost: {
    light: (props: any) => props.theme.colors.periwinkle,
    dark: (props: any) => props.theme.colors.brightCerulean,
  },
  inlineLink: {
    light: (props: any) => props.theme.colors.periwinkleDark,
    dark: (props: any) => props.theme.colors.skyBlue,
  },
});

const activeFontColor = theme.variants('mode', 'variant', {
  primary: {
    light: (props: any) => props.theme.colors.white,
    dark: (props: any) => props.theme.colors.white,
  },
  secondary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.white,
  },
  tertiary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.white,
  },
  ghost: {
    light: (props: any) => props.theme.colors.periwinkle,
    dark: (props: any) => props.theme.colors.brightCerulean,
  },
  inlineLink: {
    light: (props: any) => props.theme.colors.midnightExpress,
    dark: (props: any) => props.theme.colors.solitude,
  },
});

const disabledFontColor = theme.variants('mode', 'variant', {
  primary: {
    light: (props: any) => props.theme.colors.whiteGrey,
    dark: (props: any) => props.theme.colors.nobel,
  },
  secondary: {
    light: (props: any) => props.theme.colors.silver,
    dark: (props: any) => props.theme.colors.dimGray,
  },
  tertiary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.white,
  },
  ghost: {
    light: (props: any) => props.theme.colors.periwinkle,
    dark: (props: any) => props.theme.colors.brightCerulean,
  },
  inlineLink: {
    light: (props: any) => props.theme.colors.periwinkle,
    dark: (props: any) => props.theme.colors.brightCerulean,
  },
});

const backgroundColor = theme.variants('mode', 'variant', {
  primary: {
    light: (props: any) => props.theme.colors.safetyOrange,
    dark: (props: any) => props.theme.colors.safetyOrange,
  },
  secondary: {
    light: 'transparent',
    dark: 'transparent',
  },
  tertiary: {
    light: 'transparent',
    dark: 'transparent',
  },
  ghost: {
    light: 'transparent',
    dark: 'transparent',
  },
  inlineLink: {
    light: 'transparent',
    dark: 'transparent',
  },
});

const hoverColor = theme.variants('mode', 'variant', {
  primary: {
    light: (props: any) => props.theme.colors.tenne,
    dark: (props: any) => props.theme.colors.tenne,
  },
  secondary: {
    light: (props: any) => props.theme.colors.isabelline,
    dark: (props: any) => props.theme.colors.comet,
  },
  tertiary: {
    light: 'transparent',
    dark: 'transparent',
  },
  ghost: {
    light: (props: any) => props.theme.colors.hawkesBlue,
    dark: (props: any) => props.theme.colors.greyDarker,
  },
  inlineLink: {
    light: 'transparent',
    dark: 'transparent',
  },
});

const activeColor = theme.variants('mode', 'variant', {
  primary: {
    light: (props: any) => props.theme.colors.rust,
    dark: (props: any) => props.theme.colors.rust,
  },
  secondary: {
    light: (props: any) => props.theme.colors.greyLight,
    dark: (props: any) => props.theme.colors.comet,
  },
  tertiary: {
    light: 'transparent',
    dark: 'transparent',
  },
  ghost: {
    light: (props: any) => props.theme.colors.cloud,
    dark: (props: any) => props.theme.colors.davysGrey,
  },
  inlineLink: {
    light: 'transparent',
    dark: 'transparent',
  },
});

const disabledColor = theme.variants('mode', 'variant', {
  primary: {
    light: (props: any) => props.theme.colors.darkSalmon,
    dark: (props: any) => props.theme.colors.burntUmber,
  },
  secondary: {
    light: 'transparent',
    dark: 'transparent',
  },
  tertiary: {
    light: 'transparent',
    dark: 'transparent',
  },
  ghost: {
    light: 'transparent',
    dark: 'transparent',
  },
  inlineLink: {
    light: 'transparent',
    dark: 'transparent',
  },
});

const borderColor = theme.variants('mode', 'variant', {
  primary: {
    light: 'transparent',
    dark: 'transparent',
  },
  secondary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.whiteSmoke,
  },
  tertiary: {
    light: 'transparent',
    dark: 'transparent',
  },
  ghost: {
    light: 'transparent',
    dark: 'transparent',
  },
  inlineLink: {
    light: 'transparent',
    dark: 'transparent',
  },
});

const hoverBorderColor = theme.variants('mode', 'variant', {
  primary: {
    light: 'transparent',
    dark: 'transparent',
  },
  secondary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.white,
  },
  tertiary: {
    light: 'transparent',
    dark: 'transparent',
  },
  ghost: {
    light: 'transparent',
    dark: 'transparent',
  },
  inlineLink: {
    light: 'transparent',
    dark: 'transparent',
  },
});

const activeBorderColor = theme.variants('mode', 'variant', {
  primary: {
    light: 'transparent',
    dark: 'transparent',
  },
  secondary: {
    light: (props: any) => props.theme.colors.dimGray,
    dark: (props: any) => props.theme.colors.summerSky,
  },
  tertiary: {
    light: (props: any) => props.theme.colors.mariner,
    dark: (props: any) => props.theme.colors.pacificBlue,
  },
  ghost: {
    light: 'transparent',
    dark: 'transparent',
  },
  inlineLink: {
    light: 'transparent',
    dark: 'transparent',
  },
});

const disabledBorderColor = theme.variants('mode', 'variant', {
  primary: {
    light: 'transparent',
    dark: 'transparent',
  },
  secondary: {
    light: (props: any) => props.theme.colors.spindle,
    dark: (props: any) => props.theme.colors.dimGray,
  },
  tertiary: {
    light: 'transparent',
    dark: (props: any) => props.theme.colors.white,
  },
  ghost: {
    light: 'transparent',
    dark: 'transparent',
  },
  inlineLink: {
    light: 'transparent',
    dark: 'transparent',
  },
});

const textDecoration = theme.variants('mode', 'variant', {
  primary: {
    light: 'none',
    dark: 'none',
  },
  secondary: {
    light: 'none',
    dark: 'none',
  },
  tertiary: {
    light: 'none',
    dark: 'none',
  },
  ghost: {
    light: 'none',
    dark: 'none',
  },
  inlineLink: {
    light: 'underline',
    dark: 'underline',
  },
});

const textShadowColor = theme.variants('mode', 'variant', {
  primary: {
    light: (props: any) => props.theme.colors.harleyDavidsonOrange,
    dark: (props: any) => props.theme.colors.harleyDavidsonOrange,
  },
  secondary: {
    light: 'transparent',
    dark: 'transparent',
  },
  tertiary: {
    light: 'transparent',
    dark: 'transparent',
  },
  ghost: {
    light: 'transparent',
    dark: 'transparent',
  },
  inlineLink: {
    light: 'transparent',
    dark: 'transparent',
  },
});

const ButtonItem = styled(ButtonBase) <ButtonItemProps>`
  height: 32px;
  max-width: 100%;
  overflow: hidden;
  justify-content: center;
  transition: ${(props) => props.theme.animation.quick};
  line-height: ${(props) => props.theme.lineHeights.input};
  color: ${(props) => (props.disabled ? disabledFontColor : fontColor)};
  background-color: ${(props) => (props.disabled ? disabledColor : backgroundColor)};
  opacity: ${(props) => (props.disabled ? '0.4' : '1')};
  border-color: ${borderColor};
  display: flex;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  border-style: solid;
  border-width: 1px;
  align-items: center;
  white-space: nowrap;
  padding: ${(props) => (props.smallSized ? `${props.theme.space[1]}px ${props.theme.space[3]}px` : `${props.theme.space[2]}px ${props.theme.space[3]}px`)};
  font-weight: 700;
  font-size: 14px;
  border-radius: ${(props) => (props.smallSized ? props.theme.radius.larger : props.theme.radius.biggest)};
  text-shadow: 0px 0px 4px ${textShadowColor};
  &:hover {
    color: ${(props) => (props.disabled ? disabledFontColor : hoverFontColor)};
    background-color: ${(props) => (props.disabled ? disabledColor : hoverColor)};
    border-color: ${(props) => (props.disabled ? disabledBorderColor : hoverBorderColor)};
    text-decoration: ${textDecoration};
  }
  &:active {
    color: ${activeFontColor};
    background-color: ${activeColor};
    border-color: ${activeBorderColor};
    text-decoration: ${textDecoration};
  }
`;

const Link = styled.a<LinkProps>`
  height: 32px;
  max-width: 100%;
  overflow: hidden;
  justify-content: center;
  transition: ${(props) => props.theme.animation.quick};
  line-height: ${(props) => props.theme.lineHeights.input};
  color: ${(props) => (props.disabled ? disabledFontColor : fontColor)};
  background-color: ${(props) => (props.disabled ? disabledColor : backgroundColor)};
  opacity: ${(props) => (props.disabled ? '0.4' : '1')};
  border-color: ${borderColor};
  display: flex;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  border-style: solid;
  border-width: 1px;
  align-items: center;
  white-space: nowrap;
  padding: ${(props) => (props.smallSized ? `${props.theme.space[1]}px ${props.theme.space[2]}px` : `${props.theme.space[2]}px ${props.theme.space[3]}px`)};
  font-weight: 700;
  font-size: 14px;
  border-radius: ${(props) => props.theme.radius.biggest};
  text-shadow: 0px 0px 4px ${textShadowColor};
  text-decoration: none;
  &:hover {
    color: ${(props) => (props.disabled ? disabledFontColor : hoverFontColor)};
    background-color: ${(props) => (props.disabled ? disabledColor : hoverColor)};
    border-color: ${(props) => (props.disabled ? disabledBorderColor : hoverBorderColor)};
    text-decoration: ${textDecoration};
  }
  &:active {
    color: ${activeFontColor};
    background-color: ${activeColor};
    border-color: ${activeBorderColor};
    text-decoration: ${textDecoration};
  }
`;

const Button: FunctionComponent<ButtonProps> = ({
  variant, onClick, disabled, children, isFocused, ...otherProps
}: ButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isFocused) {
      ref?.current?.focus();
    }
  }, [isFocused]);

  return (
    <ButtonItem
      disabled={disabled}
      variant={variant}
      onClick={onClick}
      {...otherProps}
      ref={ref}
    >
      {children}
    </ButtonItem>
  );
};

const ButtonLink: FunctionComponent<ButtonLinkProps> = ({
  variant, onClick, link, disabled, children, isFocused, ...otherProps
}: ButtonLinkProps) => {
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (isFocused) {
      ref?.current?.focus();
    }
  }, [isFocused]);

  return (
    <Link
      disabled={disabled}
      variant={variant}
      onClick={onClick}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      {...otherProps}
    >
      <Flex>
        <Box mr={2}>
          {children}
        </Box>
        <ExternalLink />
      </Flex>
    </Link>
  );
};

export {
  Button,
  ButtonLink,
};
